/**
 * Presentation helpers for an in-progress session, derived entirely from
 * platform-owned `SessionSummary` fields (`players`, `currentTurnUserId`,
 * `roundNumber`) — never from game-specific state (CLAUDE.md §2, §4). Shared
 * by the dashboard's "Continue playing" section, the Games page's own
 * "Continue Playing" section (`ActiveGameCard`, `ResumeGameCard`), and the
 * History page's "vs" line, so no two surfaces can ever describe the same
 * session differently.
 */
import type { SessionSummary } from '@perplexicon/api-client';

/** Every session participant except the viewer — the platform's generic "opponent(s)" concept, not a game-specific one. */
export function opponentNames(entry: SessionSummary, selfId: string): string {
  const others = entry.players.filter((p) => p.userId !== selfId);
  if (others.length === 0) return 'Solo';
  return others.map((p) => p.username).join(', ');
}

/**
 * Whose-turn label for an active session (APP_SPEC §18: "whose turn it is
 * when applicable"), computed entirely from platform-owned fields
 * (`currentTurnUserId`, `players.length`). Returns `null` when there is no
 * single current player to name (solo, or a phase like Secret Number's
 * commit phase where both players act independently) — the game's own
 * `summary.headline` already covers that case as the session's general
 * state summary, so this never falls back to duplicating it.
 */
export function turnLabel(entry: SessionSummary, selfId: string): string | null {
  if (entry.players.length <= 1) return null;
  if (entry.currentTurnUserId === null) return null;
  return entry.currentTurnUserId === selfId
    ? 'Your turn'
    : `${opponentNames(entry, selfId)}'s turn`;
}

/** "Round N" when the game reports rounds (APP_SPEC §18: "current round when applicable"); `null` otherwise. */
export function roundLabel(entry: SessionSummary): string | null {
  return entry.roundNumber !== null ? `Round ${entry.roundNumber}` : null;
}

const RELATIVE_TIME_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 60 * 60 * 24 * 365],
  ['month', 60 * 60 * 24 * 30],
  ['week', 60 * 60 * 24 * 7],
  ['day', 60 * 60 * 24],
  ['hour', 60 * 60],
  ['minute', 60],
];

const relativeTimeFormat = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

/**
 * "18 minutes ago" / "3 hours ago" for an ISO timestamp already carried on
 * `SessionSummary` (`lastActionAt`) — a formatting choice, not a new field
 * (APP_SPEC §18's own compact resume rows want a relative phrase rather than
 * an absolute clock time). Falls back to "Just now" for anything under a
 * minute rather than "0 minutes ago".
 */
export function relativeTimeLabel(iso: string, now = Date.now()): string {
  const seconds = Math.floor((now - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  for (const [unit, unitSeconds] of RELATIVE_TIME_UNITS) {
    if (seconds >= unitSeconds) {
      return relativeTimeFormat.format(-Math.floor(seconds / unitSeconds), unit);
    }
  }
  return relativeTimeFormat.format(-Math.floor(seconds / 60), 'minute');
}
